/**
 * @file index.tsx
 *
 * @fileoverview A styled menu link item.
 */

import { cva, VariantProps } from "class-variance-authority";
import { forwardRef, LegacyRef, ReactNode, AnchorHTMLAttributes } from "react";
import styles from "./menuLink.module.scss";

const menuLink = cva([styles.menuLink], {
  variants: {
    active: {
      true: styles.menuLinkActive,
    },
  },
  defaultVariants: {
    active: false,
  },
});

export interface MenuLinkProps
  extends AnchorHTMLAttributes<HTMLAnchorElement>,
    VariantProps<typeof menuLink> {
  /**
   * The contents of the link.
   */
  children: ReactNode | string;
  /**
   * The link where the item points to.
   */
  href?: string;
  /**
   * The link element to use.
   */
  linkElement?: React.ElementType;
}

/**
 * Renders a link styled to be used as a navigation item. The theme object will provide some basic styling for
 * the element. Use this item in the main navigation of the website. it can also be used elsewhere as long as it needs to keep consistency
 * wit the main menu navigation which is the main purpose of the style of the component.
 */
export const MenuLink = forwardRef(
  (
    { children, active, linkElement = "a", ...props }: MenuLinkProps,
    ref: LegacyRef<HTMLAnchorElement>,
  ) => {
    const LinkElement = linkElement;
    return (
      <LinkElement className={menuLink({ active })} ref={ref} {...props}>
        {children}
      </LinkElement>
    );
  },
);

export default MenuLink;
